import React from 'react';
import PropTypes from 'prop-types';
import ControlP from '../../../atoms/control/ControlP';
import InputLabel from '../../../atoms/inputs/input-label';
import S3FilePicker from '../../../molecules/filepickers/s3-file-picker/S3FilePicker';
import styles from './styles.scss';
import Heading from '../../../atoms/heading';
import TooltipSpan from '../../../molecules/tooltip/TooltipSpan';
import Tooltip from '../../../molecules/tooltip/Tooltip';
import InputContainer from '../../../atoms/inputs/input-container';
import { isType, flattenChildren } from '../../../../utils/react-children-utils/react-children-utils';
import { getValidationMessage } from '../../../../utils/form-utils';
import SubmitForm from '../../../molecules/submit-form/index.ts';

class CharitySignupForm extends React.Component {
    constructor(props) {
        super(props);

        this.validateBankStatement = this.validateBankStatement.bind(this);
        this.validateCharityName = this.validateCharityName.bind(this);
        this.validateAbn = this.validateAbn.bind(this);
        this.checkForDefaultValue = this.checkForDefaultValue.bind(this);

        this.getControl = this.getControl.bind(this);
        this.getFilePicker = this.getFilePicker.bind(this);

        this.children = flattenChildren(props.children);

        this.state = {
            abnError: undefined,
            nameError: undefined,
            nameIsValid: false,
            abnIsValid: false,
            bankIsValid: false,
        };
    }

    componentDidMount() {
        // if the field is prefilled we want to reflect that in the state.
        this.children
            .filter((c) => isType(c, 'ControlP'))
            .forEach((c) => {
                this.checkForDefaultValue(c);
            });
    }

    getControl(control) {
        const { abnError, nameError } = this.state;
        // Children of the ControlP rather than this component.
        const children = flattenChildren(control.props.children);

        const label = children.find((c) => isType(c, 'InputLabel'));
        const inputContainer = children.find((c) => isType(c, 'InputContainer'));

        const labelName = inputContainer.props.name;
        const { required } = inputContainer.props;

        const validation = labelName === 'charityname'
            ? this.validateCharityName
            : this.validateAbn;

        const formError = labelName === 'charityname'
            ? nameError
            : abnError;

        const newInputs = React.cloneElement(
            inputContainer,
            {
                validation,
                required,
                formError,
            },
        );

        return (
            <ControlP key={`${labelName}-control`}>
                {label}
                {newInputs}
            </ControlP>
        );
    }

    getFilePicker() {
        const {
            bucketpath, policy, xamzcredential, xamzdate, xamzsignature, children,
        } = this.props;

        const filePickerValMsg = children.find((c) => getValidationMessage(c));

        return (
            <ControlP key="bankstatement-control">
                <InputLabel>
                    Upload your Bank Statement
                    <TooltipSpan>
                        <Tooltip>
                            <h1>Why do we need this?</h1>
                            <p>Copy of bank statement needed to verify ABN/ACN details.</p>
                        </Tooltip>
                    </TooltipSpan>
                </InputLabel>
                <InputContainer
                    passerror={this.validateBankStatement}
                    required
                    name="bankstatement"
                    displayname="Bank Statement"
                >
                    <S3FilePicker
                        maxsize={2}
                        extensions={['.png', '.jpg', '.jpeg']}
                        name="bankstatement"
                        bucketpath={bucketpath}
                        policy={policy}
                        xamzcredential={xamzcredential}
                        xamzdate={xamzdate}
                        xamzsignature={xamzsignature}
                    />
                    {filePickerValMsg}
                </InputContainer>
            </ControlP>
        );
    }

    checkForDefaultValue(control) {
        const { nameIsValid, abnIsValid } = this.state;
        const controlChildren = flattenChildren(control.props.children);
        const inputContainer = controlChildren.find((c) => isType(c, 'InputContainer'));

        const { name } = inputContainer.props;

        const input = controlChildren.find((c) => isType(c, 'input'));

        const defaultValue = input ? input.props.defaultValue ?? input.props.value : '';

        if (defaultValue) {
            if (name === 'charityname' && !nameIsValid) {
                this.validateCharityName(null, defaultValue);
            } else if (name === 'abn' && !abnIsValid) {
                this.validateAbn(null, defaultValue);
            }
        }
    }

    validateCharityName(event, prefillValue) {
        const value = event ? event.target.value : prefillValue;

        let error = '';
        let nameIsValid = false;

        if (value.length < 5 || value.length > 22) {
            error = 'Charity Name must be between 5 and 22 characters long';
        } else {
            nameIsValid = true;
        }

        this.setState(() => ({
            nameError: error,
            nameIsValid,
        }));
    }

    validateAbn(event, prefillValue) {
        const value = event ? event.target.value : prefillValue;

        const abnRegex = /^[\d\s]+$/;
        const noSpace = !value ? '' : value.replace(/\s/g, '');

        let error = '';
        let abnIsValid = false;

        if (noSpace.length < 9 || noSpace.length > 11 || !abnRegex.test(value)) {
            // ACN = 9, ABN = 11
            error = 'Please enter a valid ABN or ACN';
        } else {
            abnIsValid = true;
        }

        this.setState(() => ({
            abnError: error,
            abnIsValid,
        }));
    }

    validateBankStatement(name, error) {
        this.setState(() => ({
            bankIsValid: !error,
        }));
    }

    render() {
        const {
            abnIsValid, nameIsValid, bankIsValid,
        } = this.state;
        const { errormessage } = this.props;

        const controls = this.children
            .filter((c) => isType(c, 'ControlP'))
            .map((c) => this.getControl(c));

        const filePicker = this.getFilePicker();

        const invalid = !abnIsValid || !nameIsValid || !bankIsValid;

        return (
            <div className={styles['charity-signup-form']}>
                <Heading>Charity Details</Heading>
                <p>Fill out your details below about your Charity.</p>
                <p style={{ color: 'red', fontSize: '.75rem' }}>{errormessage}</p>
                <SubmitForm
                    formAction="CreateCharity"
                    buttonLabel="Submit"
                    name="create-charity"
                    isActionsCentered
                    isInvalid={invalid}
                >
                    {controls}
                    {filePicker}
                </SubmitForm>
            </div>
        );
    }
}

CharitySignupForm.propTypes = {
    children: PropTypes.node.isRequired,
    bucketpath: PropTypes.string.isRequired,
    xamzcredential: PropTypes.string.isRequired,
    xamzdate: PropTypes.string.isRequired,
    policy: PropTypes.string.isRequired,
    xamzsignature: PropTypes.string.isRequired,
    errormessage: PropTypes.string,
};

CharitySignupForm.defaultProps = {
    errormessage: '',
};

export default CharitySignupForm;
